import { useState } from "react";

import { Button, PasswordField, TextField, View } from "@aws-amplify/ui-react";
import { COLORS, Heading } from "@stacklet/ui";
import { Auth } from "aws-amplify";

import StackletLogo from "assets/images/logo_vertical_color_1.svg?react";

import "./Login.css";

/**
 * Path served by this page. Password reset emails link here (with the
 * verification code as a query param), so treat both the path and the param
 * name as a public contract: changing either breaks the link in every email
 * already sent.
 */
export const RESET_PASSWORD_PATH = "/reset-password";

/**
 * Query param carrying the emailed verification code. Deliberately NOT
 * `code`: with oauth configured, Amplify treats any load URL containing a
 * `code` param as an OAuth callback, consumes it, and redirects to the
 * origin before this page can render.
 */
export const RESET_CODE_PARAM = "reset-code";

/**
 * Standalone page where a user completes a password reset: they arrive from
 * the reset email (which pre-fills the verification code via `?reset-code=`)
 * and confirm the reset with their email address and a new password.
 *
 * This lives outside the Amplify Authenticator because its state machine
 * can't be deep-linked into the confirm-reset step without first sending a
 * fresh code, which would orphan the code the user was just emailed —
 * admin-initiated (resetUserPassword) codes included. Cognito accepts any
 * outstanding reset code here via forgotPasswordSubmit.
 */
export function ResetPasswordPage() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(
    () =>
      new URLSearchParams(window.location.search).get(RESET_CODE_PARAM) ?? "",
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await Auth.forgotPasswordSubmit(email.trim(), code.trim(), newPassword);
      setDone(true);
    } catch (err) {
      // Cognito's messages here are user-appropriate (wrong code, expired
      // code, password policy); show them rather than a generic failure.
      setError(
        err instanceof Error ? err.message : "Unable to reset the password",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-[1.5fr_2fr]">
      <View padding="xxxl">
        <div className="flex min-h-full flex-col items-center justify-center">
          {/* Mirrors the Authenticator's Header on the sign-in screen. */}
          <div className="mb-2xl flex flex-col items-center justify-center gap-xs">
            <StackletLogo className="stacklet-logo" height="64" width="64" />
            <Heading className="font-normal" level={2}>
              Choose a new password
            </Heading>
          </div>
          <div className="w-full max-w-[30rem] rounded-1 border border-outline-decorative bg-background-primary p-2xl text-left">
            {done ? (
              <div className="flex flex-col gap-lg">
                <p>
                  Your password has been reset. You can now sign in with it.
                </p>
                <Button
                  onClick={() => window.location.assign(window.location.origin)}
                  style={{
                    backgroundColor: COLORS.orange.base,
                    color: "white",
                  }}
                  isFullWidth
                >
                  Go to sign in
                </Button>
              </div>
            ) : (
              <form className="flex flex-col gap-lg" onSubmit={onSubmit}>
                <TextField
                  autoComplete="email"
                  label="Email"
                  onChange={(event) => setEmail(event.target.value)}
                  type="email"
                  value={email}
                  autoFocus
                  isRequired
                />
                <TextField
                  descriptiveText="The verification code from your password reset email. If it has expired, request a new one with “Reset Password” on the sign-in page."
                  label="Verification code"
                  onChange={(event) => setCode(event.target.value)}
                  value={code}
                  isRequired
                />
                <PasswordField
                  autoComplete="new-password"
                  label="New password"
                  onChange={(event) => setNewPassword(event.target.value)}
                  value={newPassword}
                  isRequired
                />
                <PasswordField
                  autoComplete="new-password"
                  errorMessage={error ?? undefined}
                  hasError={error !== null}
                  label="Confirm new password"
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  value={confirmPassword}
                  isRequired
                />
                <Button
                  isLoading={busy}
                  style={{
                    backgroundColor: COLORS.orange.base,
                    color: "white",
                  }}
                  type="submit"
                  isFullWidth
                >
                  Reset Password
                </Button>
              </form>
            )}
          </div>
        </div>
      </View>
      {/* Brand accent panel — matches the sign-in screen's. */}
      <div className="bg-neutral-925" />
    </div>
  );
}
